import * as React from "react";
import {useEffect, useState} from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {POST, GET} from "APIclient";
import properties from "constants/ApplicationConstants";

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes toastIn {
    from { opacity: 0; transform: translateX(40px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .al-page * { box-sizing: border-box; }
  .al-page { font-family: 'DM Sans', 'Segoe UI', sans-serif; }

  /* MUI DatePicker override */
  .al-datepicker .MuiInputBase-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.91rem !important;
    border-radius: 10px !important;
    background: #f8fafc !important;
    border: 1.5px solid #e2e8f0 !important;
    padding: 2px 8px !important;
  }
  .al-datepicker .MuiOutlinedInput-notchedOutline { border: none !important; }
  .al-datepicker .MuiInputBase-root:focus-within {
    border-color: #3b55e6 !important;
    box-shadow: 0 0 0 3px rgba(59,85,230,0.10) !important;
    background: #fff !important;
  }
  .al-datepicker .MuiInputLabel-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.85rem !important;
  }
  .al-datepicker .MuiFormHelperText-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.72rem !important;
  }

  /* Balance card hover */
  .al-balance-row:hover { background: #f0f4ff !important; }

  /* Select / input focus ring */
  .al-select:focus { outline: none; box-shadow: 0 0 0 3px rgba(59,85,230,0.10); }

  /* Button hover lift */
  .al-btn-primary:not(:disabled):hover { transform: translateY(-1px); }
  .al-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }

  /* ── Responsive ── */

  /* Layout: two-column on ≥ 768 px, single-column below */
  .al-grid {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 12px;
    align-items: start;
  }
  @media (max-width: 767px) {
    .al-grid {
      grid-template-columns: 1fr;
      gap: 10px;
    }
  }

  /* Page padding tightened on small screens */
  @media (max-width: 767px) {
    .al-page-wrap {
      padding: 8px 6px !important;
    }
  }

  /* Date row: two columns on ≥ 480 px, stack below */
  .al-date-row {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }
  .al-date-wrap {
    flex: 1;
    min-width: 150px;
  }
  @media (max-width: 479px) {
    .al-date-wrap {
      min-width: 100%;
    }
  }

  /* Card body padding tighter on phones */
  @media (max-width: 479px) {
    .al-card-body {
      padding: 12px 14px 14px !important;
    }
    .al-note-body {
      padding: 12px 14px !important;
    }
    .al-header-inner {
      padding: 12px 16px 10px !important;
    }
    .al-note-header-inner {
      padding: 10px 16px !important;
    }
  }

  /* Toast full-width at bottom on phones */
  @media (max-width: 479px) {
    .al-toast {
      top: auto !important;
      bottom: 16px !important;
      right: 12px !important;
      left: 12px !important;
      max-width: 100% !important;
      font-size: 0.82rem !important;
    }
  }

  /* Balance table: shrink font on phones */
  @media (max-width: 479px) {
    .al-balance-th {
      padding: 6px 8px !important;
      font-size: 0.65rem !important;
    }
    .al-balance-td {
      padding: 6px 8px !important;
      font-size: 0.80rem !important;
    }
  }

  /* Note card — push below main card; no side-by-side on mobile */
  @media (max-width: 767px) {
    .al-note-card {
      order: 2;
    }
    .al-main-card {
      order: 1;
    }
  }
`;

const s = {
  page: {
    padding: "10px",
    background: "linear-gradient(135deg, #f0f4ff 0%, #fafbff 60%, #eef2ff 100%)",
    minHeight: "100%",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif"
  },

  card: {
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: "0 24px 64px rgba(67,97,238,0.10), 0 4px 16px rgba(0,0,0,0.06)",
    background: "#fff",
    animation: "slideUp 0.45s cubic-bezier(.22,1,.36,1) both"
  },

  header: {
    background: "linear-gradient(135deg, #3b55e6 0%, #5e75f5 100%)",
    padding: "14px 22px 12px",
    position: "relative",
    overflow: "hidden"
  },
  hCircle1: {
    position: "absolute",
    width: 130,
    height: 130,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.08)",
    top: -40,
    right: -30,
    pointerEvents: "none"
  },
  hCircle2: {
    position: "absolute",
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
    bottom: -15,
    left: 15,
    pointerEvents: "none"
  },
  headerIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 7,
    backdropFilter: "blur(4px)"
  },
  headerTitle: {
    color: "#fff",
    fontSize: "1.1rem",
    fontWeight: 700,
    margin: 0,
    letterSpacing: "-0.3px",
    fontFamily: properties.fontFamily
  },
  headerSub: {
    color: "rgba(255,255,255,0.72)",
    fontFamily: properties.fontFamily,
    fontSize: "0.76rem",
    marginTop: 2,
    fontWeight: 400
  },

  body: {padding: "14px 20px 16px"},

  balanceTable: {width: "100%", borderCollapse: "collapse", marginBottom: 4},
  balanceTh: {
    padding: "7px 12px",
    background: "#f8fafc",
    fontSize: "0.70rem",
    fontWeight: 700,
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1.5px solid #e2e8f0",
    textAlign: "left"
  },
  balanceTd: {
    padding: "7px 12px",
    fontSize: "0.85rem",
    color: "#2d3748",
    borderBottom: "1px solid #f0f4f8",
    transition: "background 0.15s"
  },
  balanceBadge: val => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 36,
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: "0.78rem",
    fontWeight: 700,
    background: val > 0 ? "#ebf8ff" : "#fff5f5",
    color: val > 0 ? "#2b6cb0" : "#c53030",
    border: `1.5px solid ${val > 0 ? "#bee3f8" : "#feb2b2"}`
  }),

  divider: {height: 1, background: "#edf2f7", margin: "10px 0"},

  sectionLabel: {
    fontSize: "0.70rem",
    fontWeight: 700,
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 8
  },

  fieldGroup: {marginBottom: 10},
  label: {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#4a5568",
    marginBottom: 5,
    letterSpacing: "0.4px",
    textTransform: "uppercase"
  },
  required: {color: "#e53e3e", marginLeft: 2},

  select: {
    width: "100%",
    padding: "8px 36px 8px 12px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "0.88rem",
    color: "#2d3748",
    outline: "none",
    fontFamily: "inherit",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  selectWrap: {position: "relative"},
  selectArrow: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    color: "#a0aec0"
  },
  selectError: {borderColor: "#fc8181", background: "#fff5f5"},

  textarea: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "0.88rem",
    color: "#2d3748",
    outline: "none",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: 60,
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  textareaError: {borderColor: "#fc8181", background: "#fff5f5"},
  textareaFocus: {
    borderColor: "#3b55e6",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(59,85,230,0.10)"
  },

  errorMsg: {
    color: "#e53e3e",
    fontSize: "0.74rem",
    marginTop: 5,
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontWeight: 500
  },

  btnPrimary: {
    width: "100%",
    padding: "10px 28px",
    borderRadius: 10,
    border: "none",
    background: "linear-gradient(135deg, #3b55e6 0%, #5e75f5 100%)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.88rem",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "opacity 0.15s, transform 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    boxShadow: "0 4px 16px rgba(59,85,230,0.28)",
    marginTop: 4
  },

  noteCard: {
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(67,97,238,0.08)",
    background: "#fff",
    animation: "slideUp 0.55s cubic-bezier(.22,1,.36,1) both"
  },
  noteHeader: {
    background: "linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)",
    padding: "12px 18px",
    position: "relative",
    overflow: "hidden"
  },
  noteHCircle: {
    position: "absolute",
    width: 80,
    height: 80,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.12)",
    top: -25,
    right: -15,
    pointerEvents: "none"
  },
  noteHeaderTitle: {
    color: "#fff",
    fontFamily: properties.fontFamily,
    fontSize: "0.9rem",
    fontWeight: 700,
    margin: 0
  },
  noteBody: {padding: "14px 18px"},
  noteText: {
    fontSize: "0.82rem",
    color: "#4a5568",
    lineHeight: 1.6,
    margin: 0
  },
  noteHighlight: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#fffbeb",
    border: "1px solid #fbd38d",
    borderRadius: 8,
    padding: "6px 10px",
    marginTop: 8,
    fontSize: "0.75rem",
    color: "#744210",
    fontWeight: 600
  },

  toast: {
    position: "fixed",
    top: 24,
    right: 24,
    zIndex: 9999,
    padding: "14px 20px",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: "0.87rem",
    fontWeight: 600,
    fontFamily: "inherit",
    boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
    animation: "toastIn 0.35s cubic-bezier(.22,1,.36,1) both",
    maxWidth: 340
  },
  toastSuccess: {background: "#f0fff4", color: "#276749", border: "1.5px solid #9ae6b4"},
  toastError: {background: "#fff5f5", color: "#c53030", border: "1.5px solid #feb2b2"}
};

function FieldError({msg}) {
  if (!msg) return null;
  return (
    <div style={s.errorMsg}>
      <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      {msg}
    </div>
  );
}

export default function ApplyLeave() {
  const empId = Number(window.sessionStorage.getItem("empId"));

  const initialLeaveRequest = {
    startDate: dayjs(),
    endDate: dayjs(),
    leaveType: "",
    reason: "",
    employee: empId
  };

  const [leaveRequest, setLeaveRequest] = useState(initialLeaveRequest);
  const [leaveBalances, setLeaveBalances] = useState([
    {id: 1, LeaveType: "Planned Leave", LeavesBalance: 0},
    {id: 2, LeaveType: "Unplanned Leave", LeavesBalance: 0},
    {id: 3, LeaveType: "Compensatory Off", LeavesBalance: 0}
  ]);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({open: false, color: "", message: ""});
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [updateFlag, setUpdateFlag] = useState(false);
  const [reasonFocused, setReasonFocused] = useState(false);

  useEffect(() => {
    if (toast.open) {
      const t = setTimeout(() => setToast(s => ({...s, open: false})), 3000);
      return () => clearTimeout(t);
    }
  }, [toast.open]);

  useEffect(() => {
    if (!empId) return;
    const fetchLeavesData = async () => {
      try {
        const response = await GET(`employee/remaining-leaves/${empId}`);
        if (response.status === 200 && response.data) {
          const {plannedLeaves, unplannedLeaves, coffLeaves} = response.data.data;
          setLeaveBalances([
            {id: 1, LeaveType: "Planned Leave", LeavesBalance: plannedLeaves || 0},
            {id: 2, LeaveType: "Unplanned Leave", LeavesBalance: unplannedLeaves || 0},
            {id: 3, LeaveType: "Compensatory Off", LeavesBalance: coffLeaves || 0}
          ]);
        }
      } catch (error) {
        if (
          error.response?.status === 400 ||
          error.response?.status === 401 ||
          error.response?.status === 403
        ) {
          setToast({open: true, color: "error", message: error.response.data});
        }
      }
    };
    fetchLeavesData();
  }, [empId, updateFlag]);

  useEffect(() => {
    const valid =
      dayjs(leaveRequest.startDate).isValid() &&
      dayjs(leaveRequest.endDate).isValid() &&
      !dayjs(leaveRequest.endDate).isBefore(leaveRequest.startDate, "day") &&
      leaveRequest.leaveType &&
      leaveRequest.reason;
    setIsButtonDisabled(!valid);
  }, [leaveRequest]);

  const validateForm = () => {
    const e = {};
    if (!leaveRequest.leaveType) e.leaveType = "Leave type is required";
    if (!leaveRequest.reason) e.reason = "Reason is required";
    if (!dayjs(leaveRequest.startDate).isValid()) e.startDate = "Valid start date is required";
    if (!dayjs(leaveRequest.endDate).isValid()) e.endDate = "Valid end date is required";
    if (dayjs(leaveRequest.endDate).isBefore(leaveRequest.startDate, "day"))
      e.endDate = "End date cannot be before start date";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = e => {
    const {name, value} = e.target;
    setLeaveRequest(prev => ({...prev, [name]: value}));
    if (errors[name]) setErrors(prev => ({...prev, [name]: ""}));
  };

  const handleDateChange = (name, date) => {
    setLeaveRequest(prev => ({...prev, [name]: date}));
    const newErrors = {...errors, [name]: ""};
    if (!dayjs(date).isValid()) {
      newErrors[name] = `${name === "startDate" ? "Start" : "End"} date must be valid`;
    } else if (name === "endDate" && dayjs(date).isBefore(leaveRequest.startDate, "day")) {
      newErrors.endDate = "End date cannot be before start date";
    } else if (name === "startDate" && dayjs(leaveRequest.endDate).isBefore(date, "day")) {
      newErrors.endDate = "End date cannot be before start date";
    }
    setErrors(newErrors);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) {
      setToast({open: true, color: "error", message: "Please fill in all required fields."});
      return;
    }
    if (!empId) return;

    const payload = {
      startDate: dayjs(leaveRequest.startDate).format("YYYY-MM-DD"),
      endDate: dayjs(leaveRequest.endDate).format("YYYY-MM-DD"),
      leaveType: leaveRequest.leaveType,
      reason: leaveRequest.reason,
      employee: {empId},
      leaveAction: "Raise Request"
    };

    try {
      const response = await POST("employee/applyLeave", payload);
      if (response.status === 200) {
        setToast({open: true, color: "success", message: response.data.message});
        setLeaveRequest(initialLeaveRequest);
        setUpdateFlag(prev => !prev);
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data || "An error occurred.";
      setToast({open: true, color: "error", message: msg});
    }
  };

  const leaveTypeOptions = [
    {value: "planned", label: "Planned Leave"},
    {value: "unplanned", label: "Unplanned Leave"},
    {value: "compensatory", label: "Compensatory Leave"},
    {value: "paternity", label: "Paternity Leave"},
    {value: "lwp", label: "Leave Without Pay (LWP)"},
    {value: "onsite", label: "Onsite Leave"}
  ];

  return (
    <DashboardLayout>
      <style>{globalCSS}</style>
      <DashboardNavbar />

      <div className="al-page al-page-wrap" style={s.page}>
        {/* Two-column grid — collapses to one column on mobile via CSS class */}
        <div className="al-grid">
          {/* ── Main form card ── */}
          <div className="al-main-card" style={s.card}>
            <div className="al-header-inner" style={s.header}>
              <div style={s.hCircle1} />
              <div style={s.hCircle2} />
              <div style={s.headerIcon}>
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.2"
                  viewBox="0 0 24 24"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <p style={s.headerTitle}>Apply for Leave</p>
              <p style={s.headerSub}>Submit a leave request — your manager will review it</p>
            </div>

            <div className="al-card-body" style={s.body}>
              <p style={s.sectionLabel}>Your Leave Balance</p>
              <table style={s.balanceTable}>
                <thead>
                  <tr>
                    <th className="al-balance-th" style={s.balanceTh}>
                      #
                    </th>
                    <th className="al-balance-th" style={s.balanceTh}>
                      Leave Type
                    </th>
                    <th className="al-balance-th" style={{...s.balanceTh, textAlign: "center"}}>
                      Days Left
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leaveBalances.map(row => (
                    <tr
                      key={row.id}
                      className="al-balance-row"
                      style={{transition: "background 0.15s"}}
                    >
                      <td
                        className="al-balance-td"
                        style={{
                          ...s.balanceTd,
                          color: "#a0aec0",
                          fontSize: "0.78rem",
                          fontWeight: 600
                        }}
                      >
                        {row.id}
                      </td>
                      <td className="al-balance-td" style={s.balanceTd}>
                        {row.LeaveType}
                      </td>
                      <td className="al-balance-td" style={{...s.balanceTd, textAlign: "center"}}>
                        <span style={s.balanceBadge(row.LeavesBalance)}>{row.LeavesBalance}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={s.divider} />

              <p style={s.sectionLabel}>Select Dates</p>

              {/* Date row — uses CSS class for responsive wrapping */}
              <div className="al-date-row">
                <div className="al-date-wrap">
                  <label style={s.label}>
                    Start Date<span style={s.required}>*</span>
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="al-datepicker">
                      <DatePicker
                        format="DD/MM/YYYY"
                        value={leaveRequest.startDate}
                        onChange={date => handleDateChange("startDate", date)}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            error: !!errors.startDate,
                            helperText: errors.startDate,
                            sx: {
                              "& .MuiInputBase-root": {
                                borderRadius: "10px",
                                fontSize: "0.91rem",
                                fontFamily: "'DM Sans', sans-serif"
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </LocalizationProvider>
                </div>

                <div className="al-date-wrap">
                  <label style={s.label}>
                    End Date<span style={s.required}>*</span>
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="al-datepicker">
                      <DatePicker
                        format="DD/MM/YYYY"
                        value={leaveRequest.endDate}
                        onChange={date => handleDateChange("endDate", date)}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            error: !!errors.endDate,
                            helperText: errors.endDate,
                            sx: {
                              "& .MuiInputBase-root": {
                                borderRadius: "10px",
                                fontSize: "0.91rem",
                                fontFamily: "'DM Sans', sans-serif"
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </LocalizationProvider>
                </div>
              </div>

              <div style={s.fieldGroup}>
                <label style={s.label}>
                  Leave Type<span style={s.required}>*</span>
                </label>
                <div style={s.selectWrap}>
                  <select
                    name="leaveType"
                    value={leaveRequest.leaveType}
                    onChange={handleChange}
                    className="al-select"
                    style={{...s.select, ...(errors.leaveType ? s.selectError : {})}}
                  >
                    <option value="">Select leave type</option>
                    {leaveTypeOptions.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <span style={s.selectArrow}>
                    <svg
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
                <FieldError msg={errors.leaveType} />
              </div>

              <div style={s.fieldGroup}>
                <label style={s.label}>
                  Reason<span style={s.required}>*</span>
                </label>
                <textarea
                  name="reason"
                  value={leaveRequest.reason}
                  onChange={handleChange}
                  onFocus={() => setReasonFocused(true)}
                  onBlur={() => setReasonFocused(false)}
                  placeholder="Reason for leave..."
                  style={{
                    ...s.textarea,
                    ...(errors.reason ? s.textareaError : {}),
                    ...(reasonFocused ? s.textareaFocus : {})
                  }}
                />
                <FieldError msg={errors.reason} />
              </div>

              <button
                className="al-btn-primary"
                style={s.btnPrimary}
                onClick={handleSubmit}
                disabled={isButtonDisabled}
              >
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <path d="M22 2L11 13" />
                  <path d="M22 2L15 22 11 13 2 9l20-7z" />
                </svg>
                Submit Leave Request
              </button>
            </div>
          </div>

          {/* ── Note card ── */}
          <div className="al-note-card" style={s.noteCard}>
            <div className="al-note-header-inner" style={s.noteHeader}>
              <div style={s.noteHCircle} />
              <p style={s.noteHeaderTitle}>
                <span style={{marginRight: 8}}>📋</span>Important Note
              </p>
            </div>
            <div className="al-note-body" style={s.noteBody}>
              <p style={s.noteText}>
                While applying leaves for the <strong>current year</strong>, the balance of the
                current year will be utilised.
              </p>
              <p style={{...s.noteText, marginTop: 10}}>
                In case you are applying leave for the <strong>next year</strong> or{" "}
                <strong>previous year</strong>, it will be deducted from the respective year&apos;s
                balance.
              </p>
              <div style={s.noteHighlight}>
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                PL &amp; UPL leaves do not carry forward
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast — moves to bottom on mobile via CSS class */}
      {toast.open && (
        <div
          className="al-toast"
          style={{
            ...s.toast,
            ...(toast.color === "success" ? s.toastSuccess : s.toastError)
          }}
        >
          {toast.color === "success" ? (
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          ) : (
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          )}
          {toast.message}
        </div>
      )}
    </DashboardLayout>
  );
}
